import React, { useState, useEffect, useCallback } from 'react';
import { FaUserPlus, FaEdit, FaTrash, FaEye, FaSort, FaFilter, FaTimes, FaCheck, FaTimes as FaTimesIcon } from 'react-icons/fa';
import { db, auth } from '../firebase';
import { collection, addDoc, getDocs, deleteDoc, doc, updateDoc, query, orderBy, getDoc, where } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';

const Rosters = () => {
  const navigate = useNavigate();
  const [userRole, setUserRole] = useState(null);
  const [rosters, setRosters] = useState([]);
  const [students, setStudents] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedRoster, setSelectedRoster] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    frequency: '',
    startDate: '',
    endDate: ''
  });
  const [isFiltering, setIsFiltering] = useState(false);
  const [loadingAttendance, setLoadingAttendance] = useState({});
  const [attendanceSort, setAttendanceSort] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    students: [],
    status: 'active',
    date: new Date().toISOString().slice(0, 16),
    endDate: '',
    frequency: 'once',
    weekdays: []
  });

  const [studentFilter, setStudentFilter] = useState('all');
  const [studentSearch, setStudentSearch] = useState('');
  const [studentFilters, setStudentFilters] = useState({
    grade: '',
    stream: '',
    school: ''
  });

  const toTripTypeLabel = (tripType) => {
    if (tripType === 'AM') return 'School Trip';
    if (tripType === 'PM') return 'Home Trip';
    if (tripType === 'FIELD_TRIP') return 'Field Trip';
    return tripType || 'Trip';
  };

  const parseLocation = (location) => {
    if (!location) return null;
    const lat = Number(location.lat);
    const lng = Number(location.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    return { lat, lng };
  };

  const findStudentWaypoint = (route, student) => {
    const studentId = student?.id != null ? String(student.id) : '';
    if (!studentId) return null;

    return (route?.studentSequence || []).find((waypoint) => {
      const waypointStudentId =
        waypoint?.id ??
        waypoint?.studentId ??
        waypoint?.student?.id ??
        waypoint?.studentRef ??
        null;
      return waypoint?.location && waypointStudentId != null && String(waypointStudentId) === studentId;
    }) || null;
  };

  const getDestinationDetails = (route, student) => {
    if (!route) return { destinationName: 'Destination' };
    const endName = route?.endLocation?.placeName || route?.endLocation?.name || 'Destination';
    const endLocation = parseLocation(route?.endLocation);
    const studentWaypoint = findStudentWaypoint(route, student);
    const childDropoffLocation = parseLocation(studentWaypoint?.location);
    const childDropoffName =
      studentWaypoint?.location?.placeName ||
      studentWaypoint?.location?.name ||
      endName;

    return {
      destinationName: childDropoffLocation ? childDropoffName : endName,
      destinationLocation: childDropoffLocation || endLocation
    };
  };

  const formatExpectedArrival = (route) => {
    if (!route?.time) return 'N/A';
    const [hours, minutes] = String(route.time).split(':').map(Number);
    if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return 'N/A';

    const start = new Date();
    start.setHours(hours, minutes, 0, 0);
    const durationSeconds = Number(route?.routeMetrics?.totalDurationWithWait || 0);
    const arrival = new Date(start.getTime() + durationSeconds * 1000);

    return arrival.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const buildTripNotificationPayload = async ({ rosterId, studentId }) => {
    let student = students.find((studentItem) => studentItem.id === studentId) || null;
    if (!student) {
      const studentSnapshot = await getDoc(doc(db, 'students', studentId));
      if (studentSnapshot.exists()) {
        student = { id: studentSnapshot.id, ...studentSnapshot.data() };
      }
    }
    if (!student) {
      throw new Error(`Student record not found for notification (studentId: ${studentId})`);
    }
    if (!student?.parentId) {
      throw new Error('Student has no parentId linked');
    }

    let parentSnapshot = await getDocs(query(collection(db, 'parents'), where('parentId', '==', student.parentId)));
    if (parentSnapshot.empty) {
      const numericParentId = Number(student.parentId);
      if (Number.isFinite(numericParentId)) {
        parentSnapshot = await getDocs(query(collection(db, 'parents'), where('parentId', '==', numericParentId)));
      }
    }
    if (parentSnapshot.empty) {
      throw new Error(`No parent record found for parentId "${student.parentId}"`);
    }

    const parentDoc = parentSnapshot.docs[0];
    const parent = { id: parentDoc.id, ...parentDoc.data() };
    if (!parent?.email) {
      throw new Error(`Parent ${parent.id} has no email address`);
    }

    const routeSnapshot = await getDocs(query(collection(db, 'optimizedRoutes'), where('rosterId', '==', rosterId)));
    const routeCandidates = routeSnapshot.docs
      .map((docItem) => ({ id: docItem.id, ...docItem.data() }))
      .filter((routeItem) => routeItem.status !== 'archived');
    const route =
      routeCandidates.find((routeItem) => findStudentWaypoint(routeItem, student)) ||
      routeCandidates[0] ||
      null;

    const vehicle = route?.busId ? (await getDoc(doc(db, 'vehicles', route.busId))).data() : null;
    const driver = vehicle?.assignedDriver || null;

    const { destinationName, destinationLocation } = getDestinationDetails(route, student);
    const originLocation = parseLocation(route?.startLocation);
    const payload = {
      parentEmail: parent.email,
      parentName: `${parent.firstName || ''} ${parent.lastName || ''}`.trim() || 'Parent',
      studentName: `${student.firstName || ''} ${student.lastName || ''}`.trim() || 'Student',
      tripLabel: toTripTypeLabel(route?.tripType),
      destinationName,
      routeName: route?.name || null,
      driverName: driver?.name || 'Not assigned',
      driverPhone: driver?.phone || null,
      assistantName: vehicle?.assignedAssistant?.name || 'Not assigned',
      vehicleRegistration: vehicle?.registrationNumber || null,
      vehicleFleetNumber: vehicle?.fleetNumber || null,
      expectedArrivalTime: formatExpectedArrival(route),
      originLocation,
      destinationLocation,
      trackTripUrl: `${window.location.origin}/parent-dashboard`
    };

    return payload;
  };

  const postTripNotification = async (endpoint, payload) => {
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload)
    });

    const rawBody = await response.text();
    let result = {};
    try {
      result = rawBody ? JSON.parse(rawBody) : {};
    } catch {
      result = { message: rawBody };
    }
    if (!response.ok || !result.success) {
      console.error('Trip notification request failed', {
        endpoint,
        status: response.status,
        response: result
      });
      throw new Error(
        result.error ||
        result.message ||
        `Email API request failed with status ${response.status}`
      );
    }
  };

  const sendBoardingNotification = async ({ rosterId, studentId }) => {
    const payload = await buildTripNotificationPayload({ rosterId, studentId });
    await postTripNotification('/api/send-boarding-notification', payload);
  };

  const sendAlightingNotification = async ({ rosterId, studentId }) => {
    const payload = await buildTripNotificationPayload({ rosterId, studentId });
    await postTripNotification('/api/send-alighting-notification', {
      ...payload,
      alightedAt: new Date().toISOString()
    });
  };

  const checkAuthAndRole = useCallback(async () => {
    const user = auth.currentUser;
    
    if (!user) {
      navigate('/login');
      return;
    }

    try {
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      
      if (!userDoc.exists()) {
        navigate('/dashboard');
        return;
      }
      
      const userData = userDoc.data();
      setUserRole(userData.role);
    } catch (err) {
      console.error('Error checking user role:', err);
      navigate('/login');
    }
  }, [navigate]);

  const fetchRosters = useCallback(async () => {
    try {
      setIsFiltering(true);
      setError('');
      let q = collection(db, 'rosters');
      
      // Apply sorting
      q = query(q, orderBy(sortField, sortDirection));

      const querySnapshot = await getDocs(q);
      let rostersList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Fetch all students once
      const studentsSnapshot = await getDocs(collection(db, 'students'));
      const studentsMap = studentsSnapshot.docs.reduce((acc, doc) => {
        acc[doc.id] = { id: doc.id, ...doc.data() };
        return acc;
      }, {});

      // Add student details to each roster
      rostersList = rostersList.map(roster => ({
        ...roster,
        studentDetails: (roster.students || []).map(studentId => studentsMap[studentId]).filter(Boolean)
      }));

      // Apply search filter
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        rostersList = rostersList.filter(roster => 
          (roster.name || '').toLowerCase().includes(searchLower) ||
          (roster.description || '').toLowerCase().includes(searchLower) ||
          (roster.status || '').toLowerCase().includes(searchLower) ||
          (roster.frequency || '').toLowerCase().includes(searchLower)
        );
      }

      // Apply status filter if specified
      if (filters.status) {
        rostersList = rostersList.filter(roster => {
          const status = getRosterStatus(roster);
          return status === filters.status;
        });
      }

      // Apply frequency filter if specified
      if (filters.frequency) {
        rostersList = rostersList.filter(roster => 
          roster.frequency === filters.frequency
        );
      }

      // Apply date range filter if specified
      if (filters.startDate || filters.endDate) {
        rostersList = rostersList.filter(roster => {
          const rosterDate = new Date(roster.date);
          rosterDate.setHours(0, 0, 0, 0);

          if (filters.startDate) {
            const startDate = new Date(filters.startDate);
            startDate.setHours(0, 0, 0, 0);
            if (rosterDate < startDate) return false;
          }

          if (filters.endDate) {
            const endDate = new Date(filters.endDate);
            endDate.setHours(23, 59, 59, 999);
            if (rosterDate > endDate) return false;
          }

          return true;
        });
      }

      setRosters(rostersList);
    } catch (err) {
      console.error('Error fetching rosters:', err);
      setError('Failed to fetch rosters. Please try again.');
    } finally {
      setIsFiltering(false);
    }
  }, [sortField, sortDirection, filters.search, filters.status, filters.frequency, filters.startDate, filters.endDate]);

  const fetchStudents = useCallback(async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'students'));
      const studentsList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setStudents(studentsList);
    } catch (err) {
      console.error('Error fetching students:', err);
      setError('Failed to fetch students. Please try again.');
    }
  }, []);

  useEffect(() => {
    checkAuthAndRole();
  }, [checkAuthAndRole]);

  useEffect(() => {
    if (userRole) {
      const timeoutId = setTimeout(() => {
        fetchRosters();
      }, 300);
      return () => clearTimeout(timeoutId);
    }
  }, [userRole, fetchRosters]);

  useEffect(() => {
    if (showAddForm || showEditModal) {
      fetchStudents();
    }
  }, [showAddForm, showEditModal, fetchStudents]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      status: '',
      frequency: '',
      startDate: '',
      endDate: ''
    });
  };

  const handleViewRoster = (roster) => {
    setSelectedRoster(roster);
    setShowViewModal(true);
  };

  const handleEditRoster = (roster) => {
    setSelectedRoster(roster);
    setFormData({
      ...roster,
      students: roster.students || []
    });
    setShowEditModal(true);
    fetchStudents();
  };

  const handleDeleteRoster = async (rosterId) => {
    if (window.confirm('Are you sure you want to delete this roster?')) {
      try {
        await deleteDoc(doc(db, 'rosters', rosterId));
        setSuccess('Roster deleted successfully');
        fetchRosters();
      } catch (err) {
        console.error('Error deleting roster:', err);
        setError('Failed to delete roster');
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const newData = {
        ...prev,
        [name]: value
      };

      // If frequency changes to 'once', clear end date
      if (name === 'frequency' && value === 'once') {
        newData.endDate = '';
      }

      // If start date changes and is after end date, update end date
      if (name === 'date' && prev.endDate && value > prev.endDate) {
        newData.endDate = value;
      }

      return newData;
    });
  };

  const handleStudentToggle = (studentId) => {
    setFormData(prev => {
      const newStudents = prev.students.includes(studentId)
        ? prev.students.filter(id => id !== studentId)
        : [...prev.students, studentId];
      
      return {
        ...prev,
        students: newStudents
      };
    });
  };

  const handleWeekdayToggle = (day) => {
    setFormData(prev => ({
      ...prev,
      weekdays: prev.weekdays.includes(day)
        ? prev.weekdays.filter(d => d !== day)
        : [...prev.weekdays, day]
    }));
  };

  const getDateRangeDisplay = (roster) => {
    const startDate = new Date(roster.date);
    const endDate = roster.endDate ? new Date(roster.endDate) : null;
    
    const formatDate = (date) => {
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    };

    if (roster.frequency === 'once') {
      return formatDate(startDate);
    }

    if (endDate) {
      return `${formatDate(startDate)} - ${formatDate(endDate)}`;
    }

    return `${formatDate(startDate)} onwards`;
  };

  const getRosterStatus = (roster) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const startDate = new Date(roster.date);
    startDate.setHours(0, 0, 0, 0);
    
    const endDate = roster.endDate ? new Date(roster.endDate) : null;
    if (endDate) {
      endDate.setHours(0, 0, 0, 0);
    }

    if (roster.status === 'archived') return 'archived';
    if (endDate && endDate < today) return 'past';
    if (startDate < today && (!endDate || endDate >= today)) return 'active';
    if (startDate > today) return 'upcoming';
    return 'active';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Validate required fields
      if (!formData.name || !formData.date || !formData.frequency) {
        throw new Error('Please fill in all required fields');
      }

      // Validate date range for recurring trips
      if (formData.frequency !== 'once' && formData.endDate && formData.date > formData.endDate) {
        throw new Error('End date must be after start date');
      }

      // Validate weekdays for weekday frequency
      if (formData.frequency === 'weekdays' && (!formData.weekdays || formData.weekdays.length === 0)) {
        throw new Error('Please select at least one weekday');
      }

      const rosterData = {
        name: formData.name,
        description: formData.description || '',
        students: formData.students || [],
        status: formData.status || 'active',
        date: formData.date,
        endDate: formData.frequency !== 'once' ? (formData.endDate || null) : null,
        frequency: formData.frequency,
        weekdays: formData.frequency === 'weekdays' ? (formData.weekdays || []) : [],
        createdAt: new Date().toISOString()
      };

      // Remove any undefined values
      Object.keys(rosterData).forEach(key => {
        if (rosterData[key] === undefined) {
          delete rosterData[key];
        }
      });

      await addDoc(collection(db, 'rosters'), rosterData);
      setSuccess('Roster added successfully!');
      setShowAddForm(false);
      setFormData({
        name: '',
        description: '',
        students: [],
        status: 'active',
        date: new Date().toISOString().slice(0, 16),
        endDate: '',
        frequency: 'once',
        weekdays: []
      });
      fetchRosters();
    } catch (err) {
      console.error('Error adding roster:', err);
      setError(err.message || 'Failed to add roster. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateRoster = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Validate required fields
      if (!formData.name || !formData.date || !formData.frequency) {
        throw new Error('Please fill in all required fields');
      }

      // Validate date range for recurring trips
      if (formData.frequency !== 'once' && formData.endDate && formData.date > formData.endDate) {
        throw new Error('End date must be after start date');
      }

      // Validate weekdays for weekday frequency
      if (formData.frequency === 'weekdays' && (!formData.weekdays || formData.weekdays.length === 0)) {
        throw new Error('Please select at least one weekday');
      }

      const rosterRef = doc(db, 'rosters', selectedRoster.id);
      const updateData = {
        name: formData.name,
        description: formData.description || '',
        students: formData.students || [],
        status: formData.status || 'active',
        date: formData.date,
        endDate: formData.frequency !== 'once' ? (formData.endDate || null) : null,
        frequency: formData.frequency,
        weekdays: formData.frequency === 'weekdays' ? (formData.weekdays || []) : [],
        updatedAt: new Date().toISOString()
      };

      // Remove any undefined values
      Object.keys(updateData).forEach(key => {
        if (updateData[key] === undefined) {
          delete updateData[key];
        }
      });

      await updateDoc(rosterRef, updateData);
      setSuccess('Roster updated successfully!');
      setShowEditModal(false);
      fetchRosters();
    } catch (err) {
      console.error('Error updating roster:', err);
      setError(err.message || 'Failed to update roster');
    } finally {
      setLoading(false);
    }
  };

  const handleAttendanceToggle = async (rosterId, studentId, type) => {
    const getNextAttendance = (currentAttendance = {}) => {
      const nextValue = !currentAttendance[type];
      const nextAttendance = {
        ...currentAttendance,
        [type]: nextValue
      };

      // Keep boarded/alighted mutually exclusive for consistent trip state.
      if (type === 'boarded' && nextValue) {
        nextAttendance.alighted = false;
      } else if (type === 'alighted' && nextValue) {
        nextAttendance.boarded = false;
      }

      return nextAttendance;
    };

    // Set loading state for this specific button
    setLoadingAttendance(prev => ({
      ...prev,
      [`${rosterId}-${studentId}-${type}`]: true
    }));

    try {
      // Update local state immediately for optimistic UI update
      setRosters(prevRosters => {
        return prevRosters.map(roster => {
          if (roster.id === rosterId) {
            const attendance = roster.attendance || {};
            const studentAttendance = attendance[studentId] || {};
            const nextAttendance = getNextAttendance(studentAttendance);
            
            return {
              ...roster,
              attendance: {
                ...attendance,
                [studentId]: nextAttendance
              }
            };
          }
          return roster;
        });
      });

      // Update selected roster state if it's the one being modified
      if (selectedRoster && selectedRoster.id === rosterId) {
        setSelectedRoster(prev => {
          const attendance = prev.attendance || {};
          const studentAttendance = attendance[studentId] || {};
          const nextAttendance = getNextAttendance(studentAttendance);
          
          return {
            ...prev,
            attendance: {
              ...attendance,
              [studentId]: nextAttendance
            }
          };
        });
      }

      // Update Firestore
      const rosterRef = doc(db, 'rosters', rosterId);
      const rosterDoc = await getDoc(rosterRef);
      const rosterData = rosterDoc.data();
      
      const attendance = rosterData.attendance || {};
      const studentAttendance = attendance[studentId] || {};
      const nextAttendance = getNextAttendance(studentAttendance);
      
      // Update the roster
      await updateDoc(rosterRef, {
        attendance: {
          ...attendance,
          [studentId]: nextAttendance
        }
      });

      if (type === 'boarded' && nextAttendance.boarded) {
        try {
          await sendBoardingNotification({ rosterId, studentId });
        } catch (notificationErr) {
          console.warn('Boarding notification email was not sent:', notificationErr);
          setError(`Boarding saved, but email failed: ${notificationErr.message || 'Unknown error'}`);
          setTimeout(() => setError(''), 5000);
        }
      }

      if (type === 'alighted' && nextAttendance.alighted) {
        try {
          await sendAlightingNotification({ rosterId, studentId });
        } catch (notificationErr) {
          console.warn('Alighting notification email was not sent:', notificationErr);
          setError(`Alighting saved, but email failed: ${notificationErr.message || 'Unknown error'}`);
          setTimeout(() => setError(''), 5000);
        }
      }

      // Show success message
      setSuccess(`Student ${type === 'boarded' ? 'boarding' : 'alighting'} status updated successfully`);
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error updating attendance:', err);
      setError('Failed to update attendance');
      
      // Revert the optimistic update on error
      fetchRosters();
    } finally {
      // Clear loading state for this specific button
      setLoadingAttendance(prev => ({
        ...prev,
        [`${rosterId}-${studentId}-${type}`]: false
      }));
    }
  };

  const handleRefreshAttendance = async (rosterId) => {
    if (!window.confirm('Are you sure you want to reset all attendance data for this roster? This action cannot be undone.')) {
      return;
    }

    setLoading(true);
    try {
      // Update local state immediately for optimistic UI update
      setRosters(prevRosters => {
        return prevRosters.map(roster => {
          if (roster.id === rosterId) {
            return {
              ...roster,
              attendance: {}
            };
          }
          return roster;
        });
      });

      // Update selected roster state
      if (selectedRoster && selectedRoster.id === rosterId) {
        setSelectedRoster(prev => ({
          ...prev,
          attendance: {}
        }));
      }

      // Update Firestore
      const rosterRef = doc(db, 'rosters', rosterId);
      await updateDoc(rosterRef, {
        attendance: {}
      });

      setSuccess('Attendance data has been reset successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error resetting attendance:', err);
      setError('Failed to reset attendance data');
      
      // Revert the optimistic update on error
      fetchRosters();
    } finally {
      setLoading(false);
    }
  };

  const getAlphabeticalFilters = () => {
    const letters = students.reduce((acc, student) => {
      const firstLetter = student.firstName.charAt(0).toUpperCase();
      if (!acc.includes(firstLetter)) {
        acc.push(firstLetter);
      }
      return acc;
    }, []);
    return letters.sort();
  };

  const getStudentCounts = () => {
    return students.reduce((acc, student) => {
      const firstLetter = student.firstName.charAt(0).toUpperCase();
      acc[firstLetter] = (acc[firstLetter] || 0) + 1;
      return acc;
    }, {});
  };

  const getFilteredStudents = () => {
    let filtered = students;
    
    // Apply letter filter
    if (studentFilter !== 'all') {
      filtered = filtered.filter(student => 
        student.firstName.charAt(0).toUpperCase() === studentFilter
      );
    }
    
    // Apply search filter
    if (studentSearch) {
      const searchLower = studentSearch.toLowerCase();
      filtered = filtered.filter(student =>
        student.firstName.toLowerCase().includes(searchLower) ||
        student.lastName.toLowerCase().includes(searchLower) ||
        student.studentId.toLowerCase().includes(searchLower)
      );
    }

    // Apply grade filter
    if (studentFilters.grade) {
      filtered = filtered.filter(student => 
        student.currentGrade === studentFilters.grade
      );
    }

    // Apply stream filter
    if (studentFilters.stream) {
      filtered = filtered.filter(student => 
        student.currentStream === studentFilters.stream
      );
    }

    // Apply school filter
    if (studentFilters.school) {
      filtered = filtered.filter(student => 
        student.currentSchool === studentFilters.school
      );
    }
    
    return filtered;
  };

  const getUniqueStreams = () => {
    return [...new Set(students.map(student => student.currentStream))].sort();
  };

  const getUniqueSchools = () => {
    return [...new Set(students.map(student => student.currentSchool))].sort();
  };

  const getGroupedStudents = () => {
    const filtered = getFilteredStudents();
    return filtered.reduce((acc, student) => {
      const firstLetter = student.firstName.charAt(0).toUpperCase();
      if (!acc[firstLetter]) {
        acc[firstLetter] = [];
      }
      acc[firstLetter].push(student);
      return acc;
    }, {});
  };

  const getSortedStudents = (roster) => {
    if (!roster.studentDetails) return [];
    
    let sortedStudents = roster.studentDetails.map(student => ({
      id: student.id,
      student,
      attendance: roster.attendance?.[student.id] || {}
    }));

    // Sort based on attendance status
    if (attendanceSort !== 'all') {
      sortedStudents.sort((a, b) => {
        const aStatus = a.attendance[attendanceSort] || false;
        const bStatus = b.attendance[attendanceSort] || false;
        
        // Put unchecked items first
        if (aStatus !== bStatus) {
          return aStatus ? 1 : -1;
        }
        
        // If status is the same, sort by name
        return a.student.firstName.localeCompare(b.student.firstName);
      });
    } else {
      // Default sort by name
      sortedStudents.sort((a, b) => 
        a.student.firstName.localeCompare(b.student.firstName)
      );
    }

    return sortedStudents;
  };

  const renderRosterDetails = (roster) => {
    return (
      <div className="text-sm text-gray-500">
        <div>{roster.studentDetails?.length || 0} students</div>
        {roster.studentDetails?.slice(0, 3).map(student => (
          <div key={student.id} className="text-xs">
            {student.firstName} {student.lastName}
          </div>
        ))}
        {roster.studentDetails?.length > 3 && (
          <div className="text-xs text-gray-400">
            +{roster.studentDetails.length - 3} more
          </div>
        )}
      </div>
    );
  };

  // Pagination logic
  const totalPages = Math.ceil(rosters.length / pageSize);
  const paginatedRosters = rosters.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  if (!userRole) {
    return null;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#00E0E0] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-700">Loading Rosters...</h2>
          <p className="text-gray-500 mt-2">Please wait while we fetch your roster data</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-black">Rosters Management</h1>
          <div className="flex space-x-4">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center space-x-2 px-4 py-2 rounded-lg border border-gray-300 text-gray-700 font-semibold hover:bg-gray-50 transition-colors"
            >
              <FaFilter />
              <span>Filters</span>
            </button>
            <button
              onClick={() => setShowAddForm(true)}
              className="flex items-center space-x-2 px-4 py-2 rounded-lg bg-[#00E0E0] text-white font-semibold hover:bg-[#00B8B8] transition-colors"
            >
              <FaUserPlus />
              <span>Add New Roster</span>
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-red-500/10 border border-red-500 text-red-500 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}
        
        {success && (
          <div className="bg-green-500/10 border border-green-500 text-green-500 px-4 py-3 rounded-lg mb-6">
            {success}
          </div>
        )}

        {/* Filters */}
        {showFilters && (
          <div className="bg-white rounded-lg shadow p-4 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
                <input
                  type="text"
                  name="search"
                  value={filters.search}
                  onChange={handleFilterChange}
                  placeholder="Search rosters..."
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <select
                  name="status"
                  value={filters.status}
                  onChange={handleFilterChange}
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]"
                >
                  <option value="">All Statuses</option>
                  <option value="active">Active</option>
                  <option value="past">Past</option>
                  <option value="upcoming">Upcoming</option>
                  <option value="archived">Archived</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Frequency</label>
                <select
                  name="frequency"
                  value={filters.frequency}
                  onChange={handleFilterChange}
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]"
                >
                  <option value="">All Frequencies</option>
                  <option value="once">One-time</option>
                  <option value="daily">Daily</option>
                  <option value="weekdays">Weekdays</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Date Range</label>
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="date"
                    name="startDate"
                    value={filters.startDate}
                    onChange={handleFilterChange}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]"
                  />
                  <input
                    type="date"
                    name="endDate"
                    value={filters.endDate}
                    onChange={handleFilterChange}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]"
                  />
                </div>
              </div>
              <div className="col-span-4 flex justify-end items-center space-x-4">
                {isFiltering && (
                  <div className="text-sm text-gray-500">
                    <span className="animate-pulse">Filtering...</span>
                  </div>
                )}
                <button
                  onClick={clearFilters}
                  className="flex items-center space-x-2 px-4 py-2 rounded-lg border border-gray-300 text-gray-700 font-semibold hover:bg-gray-50 transition-colors"
                >
                  <FaTimes />
                  <span>Clear Filters</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Rosters List Controls */}
        <div className="flex items-center justify-between mb-4">
          <div className="text-sm text-gray-700 font-medium">Showing {paginatedRosters.length} of {rosters.length} rosters</div>
          <div className="flex items-center space-x-2">
            <label htmlFor="pageSize" className="text-sm text-gray-700">Rows per page:</label>
            <select
              id="pageSize"
              value={pageSize}
              onChange={e => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
              className="px-3 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0] bg-white"
              style={{ minWidth: '80px' }}
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>

        {/* Rosters List */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('name')}>
                  <div className="flex items-center space-x-1">
                    <span>Name</span>
                    <FaSort className={`text-gray-400 ${sortField === 'name' ? 'text-gray-600' : ''}`} />
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="flex items-center space-x-1">
                    <span>Description</span>
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="flex items-center space-x-1">
                    <span>Students</span>
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="flex items-center space-x-1">
                    <span>Date Range</span>
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="flex items-center space-x-1">
                    <span>Frequency</span>
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="flex items-center space-x-1">
                    <span>Status</span>
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedRosters.map((roster) => (
                <tr key={roster.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{roster.name}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-500">{roster.description}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {renderRosterDetails(roster)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {getDateRangeDisplay(roster)}
                    </div>
                    {roster.frequency === 'weekdays' && roster.weekdays?.length > 0 && (
                      <div className="text-xs text-gray-500">
                        {roster.weekdays.join(', ')}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">
                      {roster.frequency === 'once' ? 'One-time' :
                       roster.frequency === 'daily' ? 'Daily' :
                       'Weekdays'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      getRosterStatus(roster) === 'active' ? 'bg-green-100 text-green-800' :
                      getRosterStatus(roster) === 'past' ? 'bg-yellow-100 text-yellow-800' :
                      getRosterStatus(roster) === 'upcoming' ? 'bg-blue-100 text-blue-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {getRosterStatus(roster)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div className="flex space-x-3">
                      <button
                        onClick={() => handleViewRoster(roster)}
                        className="text-blue-600 hover:text-blue-900"
                        title="View Details"
                      >
                        <FaEye />
                      </button>
                      <button
                        onClick={() => handleEditRoster(roster)}
                        className="text-indigo-600 hover:text-indigo-900"
                        title="Edit Roster"
                      >
                        <FaEdit />
                      </button>
                      {(userRole !== 'driver' && userRole !== 'assistant') && (
                        <button
                          onClick={() => handleDeleteRoster(roster.id)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete Roster"
                        >
                          <FaTrash />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        <div className="flex justify-end items-center mt-4 space-x-2">
          <button
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 font-semibold bg-white hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Previous
          </button>
          <span className="text-sm text-gray-700">Page {currentPage} of {totalPages}</span>
          <button
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 font-semibold bg-white hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Next
          </button>
        </div>

        {/* Add/Edit Roster Modal */}
        {(showAddForm || showEditModal) && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-black">
                    {showAddForm ? 'Add New Roster' : 'Edit Roster'}
                  </h2>
                  <button
                    onClick={() => showAddForm ? setShowAddForm(false) : setShowEditModal(false)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    ×
                  </button>
                </div>

                <form onSubmit={showAddForm ? handleSubmit : handleUpdateRoster}>
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Name *</label>
                      <input
                        type="text"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                      <textarea
                        name="description"
                        value={formData.description}
                        onChange={handleInputChange}
                        rows={3}
                        className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]"
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Start Date *</label>
                        <input
                          type="datetime-local"
                          name="date"
                          required
                          value={formData.date}
                          onChange={handleInputChange}
                          min={new Date().toISOString().slice(0, 16)}
                          className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Frequency *</label>
                        <select
                          name="frequency"
                          required
                          value={formData.frequency}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]"
                        >
                          <option value="once">One-time Trip</option>
                          <option value="daily">Daily</option>
                          <option value="weekdays">Weekdays</option>
                        </select>
                      </div>
                    </div>

                    {formData.frequency !== 'once' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                        <input
                          type="datetime-local"
                          name="endDate"
                          value={formData.endDate}
                          onChange={handleInputChange}
                          min={formData.date}
                          className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]"
                        />
                        <p className="mt-1 text-sm text-gray-500">
                          Leave empty for ongoing trips
                        </p>
                      </div>
                    )}

                    {formData.frequency === 'weekdays' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Select Weekdays</label>
                        <div className="flex flex-wrap gap-2">
                          {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'].map(day => (
                            <button
                              key={day}
                              type="button"
                              onClick={() => handleWeekdayToggle(day)}
                              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                                formData.weekdays.includes(day)
                                  ? 'bg-[#00E0E0] text-white'
                                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                              }`}
                            >
                              {day}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Students</label>
                      
                      {/* Search and Filter Section */}
                      <div className="mb-4 space-y-4">
                        {/* Search Box */}
                        <div className="relative">
                          <input
                            type="text"
                            value={studentSearch}
                            onChange={(e) => setStudentSearch(e.target.value)}
                            placeholder="Search students by name or ID..."
                            className="w-full px-4 py-2 pl-10 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]"
                          />
                          <div className="absolute left-3 top-2.5 text-gray-400">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                          </div>
                        </div>

                        {/* Additional Filters */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Grade</label>
                            <select
                              value={studentFilters.grade}
                              onChange={(e) => setStudentFilters(prev => ({ ...prev, grade: e.target.value }))}
                              className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]"
                            >
                              <option value="">All Grades</option>
                              <option value="0000">Grade 0000</option>
                              <option value="000">Grade 000</option>
                              <option value="00">Grade 00</option>
                              <option value="0">Grade 0</option>
                              {[...Array(12)].map((_, i) => (
                                <option key={i + 1} value={i + 1}>Grade {i + 1}</option>
                              ))}
                            </select>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Stream</label>
                            <select
                              value={studentFilters.stream}
                              onChange={(e) => setStudentFilters(prev => ({ ...prev, stream: e.target.value }))}
                              className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]"
                            >
                              <option value="">All Streams</option>
                              {getUniqueStreams().map(stream => (
                                <option key={stream} value={stream}>{stream}</option>
                              ))}
                            </select>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">School</label>
                            <select
                              value={studentFilters.school}
                              onChange={(e) => setStudentFilters(prev => ({ ...prev, school: e.target.value }))}
                              className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]"
                            >
                              <option value="">All Schools</option>
                              {getUniqueSchools().map(school => (
                                <option key={school} value={school}>{school}</option>
                              ))}
                            </select>
                          </div>
                        </div>

                        {/* Alphabetical Filter */}
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={() => setStudentFilter('all')}
                            className={`px-3 py-1 rounded-full text-sm font-medium ${
                              studentFilter === 'all'
                                ? 'bg-[#00E0E0] text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                          >
                            All ({students.length})
                          </button>
                          {getAlphabeticalFilters().map(letter => {
                            const count = getStudentCounts()[letter];
                            return (
                              <button
                                key={letter}
                                type="button"
                                onClick={() => setStudentFilter(letter)}
                                className={`px-3 py-1 rounded-full text-sm font-medium ${
                                  studentFilter === letter
                                    ? 'bg-[#00E0E0] text-white'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                              >
                                {letter} ({count})
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {/* Select All Button */}
                      <div className="mb-4">
                        <button
                          type="button"
                          onClick={() => {
                            const filteredStudents = getFilteredStudents();
                            const allFilteredStudentIds = Object.values(filteredStudents).flat().map(student => student.id);
                            const allSelected = allFilteredStudentIds.every(id => formData.students.includes(id));
                            
                            if (allSelected) {
                              // If all are selected, deselect all filtered students
                              setFormData(prev => ({
                                ...prev,
                                students: prev.students.filter(id => !allFilteredStudentIds.includes(id))
                              }));
                            } else {
                              // If not all are selected, select all filtered students
                              setFormData(prev => ({
                                ...prev,
                                students: [...new Set([...prev.students, ...allFilteredStudentIds])]
                              }));
                            }
                          }}
                          className="flex items-center space-x-2 px-4 py-2 rounded-lg border border-gray-300 text-gray-700 font-semibold hover:bg-gray-50 transition-colors"
                        >
                          <input
                            type="checkbox"
                            checked={Object.values(getFilteredStudents()).flat().every(student => 
                              formData.students.includes(student.id)
                            )}
                            onChange={() => {}} // Handled by the button click
                            className="h-4 w-4 text-[#00E0E0] focus:ring-[#00E0E0] border-gray-300 rounded"
                          />
                          <span>Select All Filtered Students</span>
                        </button>
                      </div>

                      <div className="max-h-60 overflow-y-auto border border-gray-300 rounded-lg">
                        {Object.entries(getGroupedStudents()).map(([letter, students]) => (
                          <div key={letter}>
                            <div className="sticky top-0 bg-gray-50 px-3 py-2 border-b border-gray-200">
                              <span className="text-sm font-medium text-gray-700">{letter}</span>
                            </div>
                            {students.map(student => (
                              <div
                                key={student.id}
                                className="flex items-center space-x-3 p-3 hover:bg-gray-50 border-b border-gray-200 last:border-b-0"
                              >
                                <input
                                  type="checkbox"
                                  id={`student-${student.id}`}
                                  checked={formData.students.includes(student.id)}
                                  onChange={() => handleStudentToggle(student.id)}
                                  className="h-4 w-4 text-[#00E0E0] focus:ring-[#00E0E0] border-gray-300 rounded"
                                />
                                <label htmlFor={`student-${student.id}`} className="flex-1">
                                  <div className="text-sm font-medium text-gray-900">
                                    {student.firstName} {student.lastName}
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    ID: {student.studentId}
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    Grade {student.currentGrade} - {student.currentStream} at {student.currentSchool}
                                  </div>
                                </label>
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                      <select
                        name="status"
                        value={formData.status}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]"
                      >
                        <option value="active">Active</option>
                        <option value="archived">Archived</option>
                      </select>
                    </div>
                  </div>

                  <div className="flex justify-end space-x-4 mt-6">
                    <button
                      type="button"
                      onClick={() => showAddForm ? setShowAddForm(false) : setShowEditModal(false)}
                      className="px-6 py-3 rounded-lg border border-gray-300 text-gray-700 font-semibold hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className={`flex items-center space-x-2 px-6 py-3 rounded-lg bg-[#00E0E0] text-white font-semibold hover:bg-[#00B8B8] transition-colors ${
                        loading ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    >
                      {showAddForm ? <FaUserPlus /> : <FaEdit />}
                      <span>{loading ? (showAddForm ? 'Adding...' : 'Updating...') : (showAddForm ? 'Add Roster' : 'Update Roster')}</span>
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* View Roster Modal */}
        {showViewModal && selectedRoster && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] flex flex-col">
              {/* Header - Fixed */}
              <div className="p-6 border-b border-gray-200">
                <div className="flex justify-between items-center">
                  <h2 className="text-2xl font-bold text-black">Roster Details</h2>
                  <div className="flex items-center space-x-4">
                    <button
                      onClick={() => handleRefreshAttendance(selectedRoster.id)}
                      disabled={loading}
                      className={`flex items-center space-x-2 px-4 py-2 rounded-lg border border-gray-300 text-gray-700 font-semibold hover:bg-gray-50 transition-colors ${
                        loading ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                      title="Reset all attendance data"
                    >
                      {loading ? (
                        <div className="w-4 h-4 border-2 border-gray-300 border-t-[#00E0E0] rounded-full animate-spin" />
                      ) : (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                      )}
                      <span>Reset Attendance</span>
                    </button>
                    <button
                      onClick={() => setShowViewModal(false)}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      ×
                    </button>
                  </div>
                </div>
              </div>

              {/* Content - Scrollable */}
              <div className="flex-1 overflow-y-auto p-6">
                {error && (
                  <div className="bg-red-500/10 border border-red-500 text-red-500 px-4 py-3 rounded-lg mb-6">
                    {error}
                  </div>
                )}
                
                {success && (
                  <div className="bg-green-500/10 border border-green-500 text-green-500 px-4 py-3 rounded-lg mb-6">
                    {success}
                  </div>
                )}

                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold text-[#00E0E0] mb-4">Roster Information</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Name</label>
                        <p className="mt-1 text-sm text-gray-900">{selectedRoster.name}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Description</label>
                        <p className="mt-1 text-sm text-gray-900">{selectedRoster.description}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Status</label>
                        <p className="mt-1 text-sm text-gray-900">{selectedRoster.status}</p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-lg font-semibold text-[#00E0E0]">Students</h3>
                      <div className="flex items-center space-x-4">
                        <select
                          value={attendanceSort}
                          onChange={(e) => setAttendanceSort(e.target.value)}
                          className="px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0] text-sm"
                        >
                          <option value="all">Sort by Name</option>
                          <option value="boarded">Show Not Boarded First</option>
                          <option value="alighted">Show Not Alighted First</option>
                        </select>
                      </div>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Student</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Boarded</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Alighted</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {getSortedStudents(selectedRoster).map(({ id: studentId, student, attendance }) => (
                            <tr key={studentId}>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-medium text-gray-900">
                                  {student.firstName} {student.lastName}
                                </div>
                                <div className="text-xs text-gray-500">
                                  ID: {student.studentId}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <button
                                  onClick={() => handleAttendanceToggle(selectedRoster.id, studentId, 'boarded')}
                                  disabled={loadingAttendance[`${selectedRoster.id}-${studentId}-boarded`]}
                                  className={`p-2 rounded-full transition-colors duration-200 ${
                                    attendance.boarded
                                      ? 'bg-green-100 text-green-800 hover:bg-green-200'
                                      : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                                  } ${loadingAttendance[`${selectedRoster.id}-${studentId}-boarded`] ? 'opacity-50 cursor-not-allowed' : ''}`}
                                >
                                  {loadingAttendance[`${selectedRoster.id}-${studentId}-boarded`] ? (
                                    <div className="w-4 h-4 border-2 border-gray-300 border-t-[#00E0E0] rounded-full animate-spin" />
                                  ) : attendance.boarded ? (
                                    <FaCheck />
                                  ) : (
                                    <FaTimesIcon />
                                  )}
                                </button>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <button
                                  onClick={() => handleAttendanceToggle(selectedRoster.id, studentId, 'alighted')}
                                  disabled={loadingAttendance[`${selectedRoster.id}-${studentId}-alighted`]}
                                  className={`p-2 rounded-full transition-colors duration-200 ${
                                    attendance.alighted
                                      ? 'bg-green-100 text-green-800 hover:bg-gray-200'
                                      : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                                  } ${loadingAttendance[`${selectedRoster.id}-${studentId}-alighted`] ? 'opacity-50 cursor-not-allowed' : ''}`}
                                >
                                  {loadingAttendance[`${selectedRoster.id}-${studentId}-alighted`] ? (
                                    <div className="w-4 h-4 border-2 border-gray-300 border-t-[#00E0E0] rounded-full animate-spin" />
                                  ) : attendance.alighted ? (
                                    <FaCheck />
                                  ) : (
                                    <FaTimesIcon />
                                  )}
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer - Fixed */}
              <div className="p-6 border-t border-gray-200">
                <div className="flex justify-end">
                  <button
                    onClick={() => setShowViewModal(false)}
                    className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 font-semibold hover:bg-gray-50 transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Rosters; 